import { Component } from '@theme/component';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const MAX_PRODUCTS = 4;
const ERROR_MESSAGE_DISPLAY_DURATION = 4000;

/**
 * "You may also like" block in the cart drawer (`snippets/cart-drawer.liquid`).
 *
 * Loads product recommendations the first time the drawer opens, so it never delays the drawer
 * itself: complementary then related products for the newest cart product, topped up with related
 * products for the one before it. Products already in the cart are left out and the block stays
 * hidden (CSS) when none are left. Cards come from `sections/cart-drawer-recommendations.liquid`.
 *
 * The drawer re-renders through a morph on every cart change, which drops the client-rendered list
 * and refreshes `data-product-ids`. A mutation observer puts the list straight back (microtask, so
 * before paint) and reloads it when the cart's products changed.
 *
 * @extends {Component}
 */
class CartDrawerRecommendations extends Component {
  /** @type {Map<string, Promise<string>>} Section HTML by request URL, shared across re-renders */
  static #responses = new Map();

  /** @type {HTMLUListElement | null} */
  #list = null;

  /** @type {string | null} The `data-product-ids` value the current or pending list is for */
  #requestedFor = null;

  /** @type {HTMLElement | null} */
  #themeDrawer = null;

  #observer = new MutationObserver(() => this.#handleMutation());

  connectedCallback() {
    super.connectedCallback();

    this.#themeDrawer = /** @type {HTMLElement | null} */ (this.closest('theme-drawer'));
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('submit', this.#handleSubmit);
    this.#observer.observe(this, { childList: true, attributes: true, attributeFilter: ['data-product-ids'] });

    if (this.#themeDrawer?.hasAttribute('open')) this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('submit', this.#handleSubmit);
    this.#observer.disconnect();
  }

  #handleDrawerOpen = () => {
    if (this.dataset.productIds !== this.#requestedFor) this.#load();
  };

  #handleMutation() {
    if (this.#list && !this.#list.isConnected) this.append(this.#list);

    // A closed drawer refreshes on its next open instead
    if (this.dataset.productIds !== this.#requestedFor && this.#themeDrawer?.hasAttribute('open')) this.#load();
  }

  async #load() {
    const productIds = this.dataset.productIds ?? '';
    this.#requestedFor = productIds;

    const cartProductIds = productIds.split(',').filter(Boolean);
    const [newest, previous] = cartProductIds;
    if (!newest) return this.#render([]);

    const requests = [this.#fetchSection(newest, 'complementary'), this.#fetchSection(newest, 'related')];
    if (previous) requests.push(this.#fetchSection(previous, 'related'));

    const responses = await Promise.all(requests);

    // The cart changed while these were loading; a newer load owns the list
    if (this.#requestedFor !== productIds) return;

    const inCart = new Set(cartProductIds);
    /** @type {Map<string, Element>} */
    const items = new Map();

    for (const html of responses) {
      const response = new DOMParser().parseFromString(html, 'text/html');

      for (const item of response.querySelectorAll('li[data-product-id]')) {
        const id = item.getAttribute('data-product-id') ?? '';
        if (!inCart.has(id) && !items.has(id)) items.set(id, item);
      }
    }

    this.#render([...items.values()].slice(0, MAX_PRODUCTS));
  }

  /**
   * @param {string} productId
   * @param {'complementary' | 'related'} intent
   * @returns {Promise<string>} The section HTML, or an empty string when the request failed
   */
  #fetchSection(productId, intent) {
    const { url, sectionId } = this.dataset;
    const requestUrl = `${url}?product_id=${productId}&limit=10&intent=${intent}&section_id=${sectionId}`;
    const responses = CartDrawerRecommendations.#responses;

    let response = responses.get(requestUrl);
    if (!response) {
      response = fetch(requestUrl)
        .then((res) => (res.ok ? res.text() : ''))
        .catch(() => {
          // Let the next load retry instead of caching a network failure
          responses.delete(requestUrl);
          return '';
        });
      responses.set(requestUrl, response);
    }

    return response;
  }

  /** @param {Element[]} items */
  #render(items) {
    this.#list?.remove();
    this.#list = null;
    if (!items.length) return;

    const list = document.createElement('ul');
    list.className = 'cart-drawer-recommendations__list list-unstyled';
    list.setAttribute('role', 'list');
    list.append(...items);

    this.#list = list;
    this.append(list);
  }

  /**
   * Adds the card's product to the cart without leaving the drawer. Dispatches the same cart event
   * as the product form, so the drawer, header count and analytics update as for any other add.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const errorMessage = form.querySelector('.cart-drawer-recommendations__error');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = String(new FormData(form).get('id'));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].flatMap((element) =>
      element instanceof HTMLElement && element.dataset.sectionId ? [element.dataset.sectionId] : []
    );

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      const cart = await this.#fetchCart();
      const didError = Boolean(response.status);

      if (didError) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#showError(errorMessage, response.description || response.message);
      }

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          source: 'cart-drawer-recommendations',
          sections: didError ? undefined : response.sections,
          didError,
        },
      });
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /** @returns {Promise<any>} The AJAX cart */
  async #fetchCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /**
   * @param {Element | null} element
   * @param {string} [message]
   */
  #showError(element, message) {
    if (!(element instanceof HTMLElement) || !message) return;

    element.textContent = message;
    element.hidden = false;
    setTimeout(() => (element.hidden = true), ERROR_MESSAGE_DISPLAY_DURATION);
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
